import { Injectable } from '@angular/core';
import { Observable, of, timer, switchMap, delay } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class RequestQueueService {
  private requestQueue: Array<() => Observable<any>> = [];
  private isProcessing = false;
  private requestDelay = 300; // 300ms between requests

  /**
   * Queues a request to be executed with proper throttling
   * @param requestFn Function that returns an Observable
   * @returns Observable that will emit the result of the request
   */
  queueRequest<T>(requestFn: () => Observable<T>): Observable<T> {
    return new Observable<T>(observer => {
      const executeRequest = (): Observable<any> => {
        return new Observable<any>(requestObserver => {
          requestFn().pipe(
            delay(this.requestDelay) // Add delay after each request
          ).subscribe({
            next: (result) => {
              observer.next(result);
              observer.complete();
              requestObserver.next(result);
              requestObserver.complete();
              this.processNextRequest();
            },
            error: (error) => {
              observer.error(error);
              requestObserver.error(error);
              this.processNextRequest();
            }
          });
        });
      };

      // Add to queue
      this.requestQueue.push(executeRequest);
      
      // Start processing if not already processing
      if (!this.isProcessing) {
        this.processNextRequest();
      }
    });
  }

  private processNextRequest(): void {
    if (this.requestQueue.length === 0) {
      this.isProcessing = false;
      return;
    }

    this.isProcessing = true;
    const nextRequest = this.requestQueue.shift();
    if (nextRequest) {
      nextRequest().subscribe(); // Subscribe to the Observable
    }
  }

  /**
   * Clears the request queue
   */
  clearQueue(): void {
    this.requestQueue = [];
    this.isProcessing = false;
  }

  /**
   * Gets the current queue length
   */
  getQueueLength(): number {
    return this.requestQueue.length;
  }
} 